<template>
  <div class="opportunity-mobilizations">
    <template>
      <div
        v-for="mobilization in opportunity.mobilizations"
        :key="mobilization.id"
        class="opportunity-mobilizations__item"
        :class="{ selected: minDurationMobilization && mobilization.id === minDurationMobilization.id }"
      >
        <template>
          <div class="opportunity-mobilizations__item-left-section">
            <BaseIcon class="opportunity-mobilizations__icon" name="map-pin" />
            <span class="opportunity-mobilizations__text">{{ mobilization.trajectoryName }}</span>
          </div>
          <div class="opportunity-mobilizations__item-right-section">
            <span class="opportunity-mobilizations__subtext">{{ distance(mobilization.distance) }}</span>
            <span class="opportunity-mobilizations__text">{{ time(mobilization.duration) }}</span>
          </div>
        </template>
      </div>
      <BaseMap
        v-if="minDurationMobilization"
        class="opportunity-mobilizations__map"
        :center="mapCenter"
        :markers="[minDurationMobilization.fromLocation, minDurationMobilization.toLocation]"
        :route-coordinates="minDurationMobilizationDirections"
      />
      <BaseMap
        v-else-if="showLocationIfNoRoute && opportunity.jobLocation"
        class="opportunity-mobilizations__map"
        :center="opportunity.jobLocation"
        :markers="[opportunity.jobLocation]"
        :zoom="4"
      />
    </template>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseMap from '@/components/base/BaseMap.vue';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'OpportunityMobilizations',
  components: {
    BaseMap,
    BaseIcon,
  },
  props: {
    opportunity: Object,
    showLocationIfNoRoute: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('opportunityMobilizations', ['directions']),
    minDurationMobilization() {
      if (this.opportunity.mobilizations.length === 0) {
        return null;
      }
      return this.opportunity.mobilizations.reduce((min, mob) => {
        if (mob.duration == null) {
          return min;
        }
        if (min.duration == null) {
          return mob;
        }
        return Number.parseFloat(mob.duration) < Number.parseFloat(min.duration) ? mob : min;
      }, this.opportunity.mobilizations[0]);
    },
    minDurationMobilizationId() {
      return this.minDurationMobilization ? this.minDurationMobilization.id : null;
    },
    minDurationMobilizationDirections() {
      if (!this.directions || !this.directions.routes || this.directions.routes.length === 0) {
        return [];
      }
      return this.directions.routes[0].geometry.coordinates;
    },
    mapCenter() {
      // map center is the center point between the fromLocation and the toLocation
      return {
        longitude:
          (Number.parseFloat(this.minDurationMobilization.fromLocation.longitude) +
            Number.parseFloat(this.minDurationMobilization.toLocation.longitude)) /
          2,
        latitude:
          (Number.parseFloat(this.minDurationMobilization.fromLocation.latitude) +
            Number.parseFloat(this.minDurationMobilization.toLocation.latitude)) /
          2,
      };
    },
  },
  methods: {
    ...mapActions('opportunityMobilizations', ['getOpportunityMobilizationDirections']),
    distance(meters) {
      if (meters === null) {
        return '-';
      }
      return `${(meters / 1000).toFixed(1)} km`;
    },
    time(seconds) {
      if (seconds === null) {
        return '-';
      }
      return `${(seconds / (60 * 60)).toFixed(1)} h`;
    },
    onAddJobLocationClick() {
      this.$emit('add-job-location-click');
    },
  },
  watch: {
    minDurationMobilizationId: {
      immediate: true,
      handler(mobilizationId) {
        if (!mobilizationId) return;
        this.getOpportunityMobilizationDirections({
          opportunityId: this.opportunity.id,
          mobilizationId,
        });
      },
    },
  },
};
</script>

<style lang="scss">
.opportunity-mobilizations {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 8px;

  &__empty-state {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__item {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    border-radius: 8px;

    &-left-section {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;
    }

    &-right-section {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;
    }

    &.selected {
      background: $color-accent-100;
    }
  }

  &__icon {
    color: $color-dark-blue-pale;
    width: 18px;
    height: 18px;
  }

  &__text {
    color: $color-normal;
    font-size: 14px;
    font-weight: 700;
  }

  &__subtext {
    color: $color-dark-blue-pale;
    font-size: 14px;
  }

  &__map {
    height: 290px;
  }
}
</style>
